import React, { useEffect, useState } from 'react';
import { Button, Flex, Form, message, Pagination, Popconfirm, Space, Table, Tag } from 'antd';
import type { TableColumnType } from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { PageLink } from '@/config/pageConfig';

// 接口文档地址
// https://supptech.sg.larksuite.com/wiki/Nobsw0FhdilBpak9V2nlUc29gxh

export const CONTENT_REVIEW_IDS_KEY = 'content_review_ids';
export const CONTENT_REVIEW_INDEX_KEY = 'content_review_index';

// import { StatusEnm, StatusMap } from './type';
import { useRequest, useUpdateEffect } from 'ahooks';
import type { RecallEmailIListResp, RecallEmailListReq } from '@/types/recall';
import { cancel, listCustom } from '@/api/recall';
import { PublishStatusMap, PublishStatusEnum } from './type';

export const ActivityStatusColorMap = {
  [PublishStatusEnum.SUCC]: 'green',
  [PublishStatusEnum.WAIT]: 'gray',
  [PublishStatusEnum.CANCEL]: 'red',
};

const Recall: React.FC = () => {
  const [form] =
    Form.useForm<
      Omit<ISearchParams, 'currentPage' | 'pageSize' | 'reviewStatus'>
    >();
  const isMobile = useAtomValue(isMobileAtom);
  const pageSize = isMobile ? 10 : 20;
  const [page, setPage] = useState(1);
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const formValues: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      if (key === 'channelDictId') {
        formValues[key] = parseInt(value);
      } else {
        formValues[key] = value;
      }
    });
    form.setFieldsValue(formValues);
    const page = searchParams.get('page') || '1';
    setPage(parseInt(page));
  }, [searchParams, form]);

  const { data, loading, run, refresh } = useRequest(
    async (page: number = 1) => {
      const params: RecallEmailListReq = {
        currentPage: page,
        pageSize,
      };
      const resp = await listCustom(params);

      if (resp.status === 200 && resp.data.status === 200) {
        return resp.data.data;
      }

      return {
        records: [],
        total: 0,
        currentPage: page,
        pageSize,
      };
    }
  );

  useUpdateEffect(() => {
    run(page);
  }, [page]);

  const onCancel = async (id) => {
    const res = await cancel([id]);
    if (res.status !== 200 || res.data.status !== 200) {
      return message.error(res.data.message);
    }

    message.success(res.data.message);
    refresh();
  };

  const columns: TableColumnType<RecallEmailIListResp>[] = [
    {
      title: 'id',
      dataIndex: 'recallEmailId',
      key: 'id',
    },
    {
      title: '推送标题',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '推送状态',
      dataIndex: 'publishStatus',
      key: 'publishStatus',
      render: (_: PublishStatusEnum) => {
        return (
          <Tag color={ActivityStatusColorMap[_]}>
            {PublishStatusMap.get(_) as string}
          </Tag>
        );
      },
    },
    {
      title: '推送时间',
      dataIndex: 'publishTime',
      key: 'publishTime',
      render: (_: string) => {
        return _ || '-';
      },
    },
    {
      title: '操作人员',
      dataIndex: 'publishUser',
      key: 'publishUser',
      render: (_: string) => _ || '-',
    },
    {
      title: '操作',
      key: 'operation',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Button
            type="link"
            onClick={() =>
              navigate(`${PageLink.Recall}/detail/${record.recallEmailId}`)
            }
          >
            查看详情
          </Button>
          {record.publishStatus === PublishStatusEnum.WAIT && (
            <Popconfirm
              title="取消发布确认"
              description="确定要取消发布吗？"
              onConfirm={() => onCancel(record.recallEmailId)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" danger>
                取消发布
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="content-review-container">
      {contextHolder}
      <div className="table-container">
        <Space size="middle" className="table-header">
          <Button
            type="primary"
            onClick={() => navigate(`${PageLink.Recall}/create`)}
          >
            创建
          </Button>
        </Space>
        <Table
          size="small"
          loading={loading}
          rowKey="recallEmailId"
          key="recall"
          columns={columns}
          dataSource={data?.records || []}
          scroll={{ x: 'max-content' }}
          pagination={false}
        />

        <Flex
          className="table-pagination"
          justify="space-between"
          style={{ flexDirection: isMobile ? 'row-reverse' : 'row' }}
        >
          <Pagination
            size={isMobile ? 'small' : 'default'}
            total={data?.total}
            pageSize={pageSize}
            current={page}
            onChange={(page) => {
              setPage(page);
              const params = new URLSearchParams(searchParams);
              params.set('page', page.toString());
              setSearchParams(params);
            }}
            showSizeChanger={false}
          />
        </Flex>
      </div>
    </div>
  );
};

export default Recall;
